import db from "../db/database";
import {
    fetchFromBusinessCentral,
    sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { getPendingSync, isOnline } from "./syncService";

// Función para transformar un producto al formato esperado por Business Central
const transformProductForBC = (product) => {
    // Transformar el producto al formato esperado por BC
    const transformedProduct = {
        No_: product.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
        Description: product.description,
        Unit_Price: product.Unit_Price || 0, // Incluir el precio unitario
        PorIva: product.PorIva || 0, // Incluir el porcentaje de IVA
        Deleted: product.deleted,
    };

    // Crear el formato final esperado por el endpoint
    return {
        data: JSON.stringify({ Items: [transformedProduct] }),
    };
};

export const syncProducts = async () => {
    if (!isOnline()) {
        return false;
    }

    try {
        const pendingProducts = await getPendingSync("products");

        // Enviar productos pendientes uno por uno
        if (pendingProducts.length > 0) {
            console.log(`Sincronizando ${pendingProducts.length} productos pendientes...`);

            for (const product of pendingProducts) {
                try {
                    // Si el producto está marcado como eliminado, lo eliminamos directamente
                    if (product.deleted) {
                        await db.products.delete(product.id);
                        continue;
                    }

                    // Enviar producto individual a Business Central
                    const response = await sendToBusinessCentral(
                        "Importaciones_insertaProductos",
                        transformProductForBC(product),
                        true
                    );

                    // Verificar respuesta y actualizar el producto con el No asignado por BC
                    if (response && response.value) {
                        const productNo = response.value;

                        console.log(`Producto ${product.description} (ID: ${product.id}) sincronizado con No: ${productNo}`);

                        // Actualizar el producto local con el No de BC
                        await db.products.update(product.id, {
                            No: productNo,
                            lastModified: new Date(),
                            lastSynced: new Date(),
                            pendingSync: 0,
                        });

                    } else {
                        console.error(`Error al sincronizar producto ${product.id}: Respuesta inválida de BC`);
                    }
                } catch (error) {
                    console.error(`Error al sincronizar producto ${product.id}:`, error);
                }
            }
        }

        const localProducts = await db.products.toArray();

        // Obtener el último Source_Counter guardado
        let lastSourceCode = 0;

        if (localProducts.length > 0) {
            lastSourceCode = localProducts.sort((a, b) => a.Source_Counter - b.Source_Counter)[localProducts.length - 1].Source_Counter;
        }

        console.log("lastSourceCode", lastSourceCode);

        // Obtener productos del servidor después de sincronizar los pendientes
        await fetchProductsBySourceCounter(lastSourceCode, 1000);

        return true;
    } catch (error) {
        console.error("Error al sincronizar productos:", error);
        return false;
    }

    async function fetchProductsBySourceCounter(lastCounter = 0, pageSize = 1000) {
        let hasMore = true;

        while (hasMore) {
            const filterOperator = lastCounter === 0 ? "ge" : "gt";
            const url = `ListaProductos?$filter=Source_Counter ${filterOperator} ${lastCounter} and Blocked eq false&$orderby=Source_Counter asc&$top=${pageSize}`;

            const response = await fetchFromBusinessCentral(url);
            if (!response) break;

            const data = response.value || response;
            if (!data || data.length === 0) break;

            lastCounter = data[data.length - 1].Source_Counter;

            // Si trajo menos de pageSize, ya llegamos al final
            hasMore = data.length === pageSize;

            await saveProductsBatch(data)

            // Espera mínima entre requests (para no saturar BC)
            if (hasMore) {
                await new Promise(r => setTimeout(r, 800));
            }

            console.log(`Rango recibido: ${data[0].Source_Counter} → ${data[data.length - 1].Source_Counter}`);
        }
    }

    async function saveProductsBatch(products) {
        await db.transaction("rw", db.products, async () => {
            for (const p of products) {
                const data = {
                    ...p,
                    No: p.No,
                    description: p.Description,
                    Unit_Price: p.Unit_Price || 0,
                    PorIva: p.PorIva || 0,
                    lastSynced: new Date(),
                    pendingSync: 0,
                    deleted: false,
                };

                const existing = await db.products.get(p.No);

                if (existing) {
                    if (existing.pendingSync === 0) await db.products.put({ ...existing, ...data });
                } else {
                    await db.products.add(data);
                }
            }
        });
    }
}; 